import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import UserContext from "../../context/UserContext";
import axiosInstance from "../../config/axiosInstance";
import { HiX } from "react-icons/hi";
import { 
  HiMiniHome,
  HiMiniUsers,
  HiMiniRectangleStack,
  HiMiniBanknotes,
  HiMiniSquares2X2,
  HiMiniBuildingOffice2,
  HiMiniCreditCard,
  HiMiniCog6Tooth,
  HiMiniLink
} from "react-icons/hi2";

export default function Sidebar({ mobileOpen, setMobileOpen }) {
  const { user, logout, getUserData } = useContext(UserContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const [customLinks, setCustomLinks] = useState([]);
  const location = useLocation();

  useEffect(() => {
    getUserData();
  }, []);

  useEffect(() => {
    if (user) {
      setIsAdmin(Number(user.role) === 1);
    }
  }, [user]);

  useEffect(() => {
    const fetchCustomLinks = async () => {
      try {
        const response = await axiosInstance.get('/admin/custom-links');
        setCustomLinks(response.data.links || []);
      } catch (error) {
        console.log(error);
      }
    };

    if (user && user.userId) {
      fetchCustomLinks();
    }
  }, [user, location.pathname]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (to) => location.pathname.startsWith(to);

  const navLink = (to, label, Icon) => (
    <Link
      to={to}
      className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors
        ${isActive(to) ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"}`}
    >
      {Icon && <Icon className="h-4 w-4" />}
      <span className="truncate">{label}</span>
    </Link>
  );

  return (
    <div
      className={`fixed top-0 left-0 h-full z-50 w-64 bg-white/80 dark:bg-gray-900/80 backdrop-blur border-r border-gray-200 dark:border-gray-700 shadow-sm transform transition-transform duration-300 ease-in-out
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0 lg:static lg:flex lg:flex-col`}
    >
      <div className="relative px-4 py-6 overflow-y-auto h-full">
        <button
          className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 text-2xl lg:hidden"
          onClick={() => setMobileOpen(false)}
        >
          <HiX />
        </button>

        <div className="mb-6 flex items-center gap-2 px-2">
          <div className="h-8 w-8 rounded-md bg-gray-900 dark:bg-gray-700 text-white grid place-items-center text-xs font-bold">CO</div>
          <h2 className="font-semibold text-base tracking-tight text-gray-900 dark:text-gray-100">CreativeOcto.io</h2>
        </div>

        <ul className="space-y-1 mt-8 md:mt-0">
          <li>{navLink("/manage/dashboard", "Dashboard", HiMiniHome)}</li>
          <li>
            <details className="group [&_summary::-webkit-details-marker]:hidden">
              <summary className="flex items-center justify-between px-3 py-2 text-sm rounded-lg cursor-pointer text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100">
                <span className="flex items-center gap-3">
                  <HiMiniBuildingOffice2 className="h-4 w-4" />
                  Clients
                </span>
                <svg className="h-5 w-5 transition-transform group-open:-rotate-180" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.061l-4.24 4.25a.75.75 0 01-1.06 0l-4.24-4.25a.75.75 0 01.02-1.06z" />
                </svg>
              </summary>
              <ul className="mt-2 space-y-1 px-3">
                <li>{navLink("/manage/client/company", "Companies", HiMiniBuildingOffice2)}</li>
                <li>{navLink("/manage/client/brands", "Brands", HiMiniSquares2X2)}</li>
                <li>{navLink("/manage/client/users", "Users", HiMiniUsers)}</li>
              </ul>
            </details>
          </li>



          <li>{navLink("/manage/projects", "Projects", HiMiniRectangleStack)}</li>

          {isAdmin && (
            <>
              <li>{navLink("/manage/billing/all", "Billing", HiMiniBanknotes)}</li>
              <li>{navLink("/manage/users", "Users", HiMiniUsers)}</li>
              <li>{navLink("/manage/packages", "Packages", HiMiniSquares2X2)}</li>
              <li>{navLink("/manage/subscriptions", "Subscriptions", HiMiniCreditCard)}</li>
              <li>
                <details className="group [&_summary::-webkit-details-marker]:hidden">
                  <summary className="flex items-center justify-between px-3 py-2 text-sm rounded-lg cursor-pointer text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100">
                    <span className="flex items-center gap-3">
                      <HiMiniSquares2X2 className="h-4 w-4" />
                      Website CMS
                    </span>
                    <svg className="h-5 w-5 transition-transform group-open:-rotate-180" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.061l-4.24 4.25a.75.75 0 01-1.06 0l-4.24-4.25a.75.75 0 01.02-1.06z" />
                    </svg>
                  </summary>
                  <ul className="mt-2 space-y-1 px-3">
                    <li><a href="/manage/leads/contactform" className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100">Contact Us Leads</a></li>
                    <li><a href="/manage/leads/demo" className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100">Demo Leads</a></li>
                    <li><a href="/manage/portfolio" className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100">Manage Portfolio</a></li>
                  </ul>
                </details>
              </li>
              <li>{navLink("/manage/settings", "Settings", HiMiniCog6Tooth)}</li>
            </>
          )}

          {customLinks.length > 0 && (
            <li className="pt-4">
              <div className="px-3 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Other Links
              </div>
              <ul className="space-y-1">
                {customLinks.map((link) => (
                  <li key={link.id}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
                    >
                      <HiMiniLink className="h-4 w-4 shrink-0" />
                      <span className="truncate">{link.link_name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
